import React from 'react';
import { Job, FinancialRecord, LineItem } from '../types';
import { Logo, BrandName } from './Logo';

interface Props {
  job?: Job;
  record?: FinancialRecord;
  type: 'ESTIMATE' | 'INVOICE';
}

const EstimateTemplate: React.FC<Props> = ({ job, record, type }) => {
  const clientName = job?.clientName || 'No Data';
  const address = job?.address || 'No Data';
  const email = job?.email || 'No Data';
  const phone = job?.phone || 'No Data';
  const date = record?.date || new Date().toISOString();
  const dueDate = record?.dueDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  const id = record?.id || job?.id || 'PENDING';
  const lineItems = record?.lineItems || job?.lineItems || [];
  const total = record?.amount || job?.totalPrice || 0;

  // Status line as it appears on the owner's own estimate form.
  const statusLabel = type === 'ESTIMATE'
    ? (record?.status === 'PAID' ? 'Approved' : 'Pending Confirmation')
    : (record?.status === 'PAID' ? 'Paid' : 'Due');

  return (
    <div className="bg-white p-12 max-w-4xl mx-auto shadow-2xl border border-stone-100 font-sans text-bark min-h-[1056px] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-start mb-12">
        <div>
          <div className="flex items-center space-x-3 mb-4">
            <Logo className="w-12 h-12" />
            <BrandName className="text-xl text-bark" />
          </div>
          <div className="text-xs font-bold text-stone-500 space-y-1">
            <p>4314 Fay Drive, Columbus, GA 31907</p>
            <p>greentreearboriculture.com</p>
            <p>706-326-7034</p>
            <p>mrgreen@greentreearboriculture.com</p>
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-5xl font-black uppercase tracking-tighter mb-2 text-stone-200">{type}</h2>
          <div className="space-y-3">
            <div className="space-y-1">
              <p className="text-xs font-black uppercase tracking-widest text-stone-400">Number</p>
              <p className="font-mono font-bold text-lg">#GT-{id.substring(0, 8).toUpperCase()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-black uppercase tracking-widest text-stone-400">Status</p>
              <p className="font-bold text-sm">{statusLabel}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Client Info */}
      <div className="grid grid-cols-2 gap-12 mb-12">
        <div>
          <h3 className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-3">Bill To</h3>
          <div className="space-y-1">
            <p className="font-black text-xl">{clientName}</p>
            <p className="text-sm font-bold text-stone-600">{address}</p>
            <p className="text-sm font-medium text-stone-500">{email}</p>
            <p className="text-sm font-medium text-stone-500">{phone}</p>
          </div>
        </div>
        <div className="flex justify-end">
          <div className="space-y-4 text-right">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">Date Issued</p>
              <p className="font-bold">{new Date(date).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">{type === 'ESTIMATE' ? 'Valid Until' : 'Due Date'}</p>
              <p className="font-bold">{new Date(dueDate).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="flex-grow">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b-2 border-bark">
              <th className="py-4 text-[10px] font-black uppercase tracking-widest text-stone-400 w-10">#</th>
              <th className="py-4 text-[10px] font-black uppercase tracking-widest text-stone-400">Item</th>
              <th className="py-4 text-[10px] font-black uppercase tracking-widest text-stone-400 text-center w-20">Qty/Hrs</th>
              <th className="py-4 text-[10px] font-black uppercase tracking-widest text-stone-400 text-right w-32">Amount/Rate</th>
              <th className="py-4 text-[10px] font-black uppercase tracking-widest text-stone-400 text-right w-32">Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {lineItems.length > 0 ? (
              lineItems.map((item, idx) => (
                <tr key={item.id}>
                  <td className="py-6 text-stone-400 font-bold">{idx + 1}</td>
                  <td className="py-6">
                    <p className="font-bold text-bark">{item.description}</p>
                  </td>
                  <td className="py-6 text-center font-bold text-stone-600">{item.quantity}</td>
                  <td className="py-6 text-right font-bold text-stone-600">${item.unitPrice.toLocaleString()}</td>
                  <td className="py-6 text-right font-black text-bark">${(item.quantity * item.unitPrice).toLocaleString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="py-12 text-center text-stone-400 italic font-medium">
                  No items listed. Please contact us for details.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="mt-12 pt-8 border-t-2 border-stone-100">
        <div className="flex justify-end">
          <div className="w-64 space-y-3">
            <div className="flex justify-between text-sm font-bold text-stone-500">
              <span>Subtotal</span>
              <span>${total.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center pt-4 border-t border-stone-100">
              <span className="text-[10px] font-black uppercase tracking-widest text-bark">Total Amount</span>
              <span className="text-3xl font-black text-gecko">${total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Terms — verbatim from the owner's own estimate form */}
      <div className="mt-12 pt-8 border-t-2 border-stone-100">
        <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-4">Terms and Conditions for Tree Service Estimates</h4>
        <div className="text-[9px] font-medium text-stone-500 space-y-3 leading-relaxed text-justify">
          <p><span className="font-black text-stone-600">Acceptance and Validity:</span> This estimate is valid for 30 days from the issue date. Approval of this estimate via signature, electronic confirmation, or written authorization constitutes a binding contract for the services specified.</p>
          <p><span className="font-black text-stone-600">Scope of Work & Debris Disposal:</span> Work is strictly limited to the items detailed in the estimate. Debris cleanup includes standard raking and removal of brush and logs generated by the work, but excludes underground root systems or stumps unless explicitly listed as a separate line item.</p>
          <p><span className="font-black text-stone-600">Property Access and Ownership:</span> The customer warrants that all trees and work areas are located on their property or that they have secured explicit, written permission from the property owner. The customer agrees to provide clear access for vehicles, equipment, and personnel, and is responsible for accurately locating and marking all private underground utilities, sprinkler systems, and invisible fences.</p>
          <p><span className="font-black text-stone-600">Limitation of Liability:</span> GreenTree Arboriculture maintains liability and workers' compensation coverage. However, the contractor is not responsible for pre-existing property defects, damage to unmarked underground lines, or ordinary, unavoidable turf rutting and compaction caused by heavy equipment under normal operating conditions. Furthermore, while arboricultural recommendations minimize risk, tree care involves inherent natural hazards; the contractor is not liable for structural failures or storm damage occurring after services are completed.</p>
          <p><span className="font-black text-stone-600">Payment Terms:</span> Payment is due in full within 30 days of project completion unless otherwise agreed in writing. Overdue balances are subject to a late finance charge of 2% per month (or the maximum rate permitted by law). Unpaid accounts may result in the filing of a construction lien against the property.</p>
          <p><span className="font-black text-stone-600">Cancellation Policy:</span> Cancellations for scheduled jobs require at least 24 hours' advance notice. The contractor reserves the right to charge for any non-refundable expenses incurred in preparation for the project (such as specialized equipment or permit fees) if canceled past this window.</p>
        </div>
      </div>

      {/* Signatures */}
      <div className="mt-10 pt-8 border-t border-stone-100 grid grid-cols-2 gap-12">
        <div>
          <div className="w-48 border-b border-stone-300 mb-2 h-12"></div>
          <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">Customer Acceptance</p>
        </div>
        <div className="flex justify-end">
          <div className="text-right">
            <div className="w-48 border-b border-stone-300 mb-2 h-12"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">Authorized Signature</p>
          </div>
        </div>
      </div>

      <div className="mt-10 text-center space-y-2">
        <p className="text-[10px] font-bold text-stone-400">
          Please follow this link to give us a review on Google:{' '}
          <a
            href="https://g.page/r/Ccypclex1Yp1EBM/review"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gecko hover:underline break-all"
          >
            https://g.page/r/Ccypclex1Yp1EBM/review
          </a>
        </p>
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-stone-300">Thank you!</p>
      </div>
    </div>
  );
};

export default EstimateTemplate;
